/**
 * Lift2Fit — the coach chat thread, shared by both ends of the relationship.
 *
 * The lifter renders it inside `#/my-coach` → Chat; the coach renders the same
 * component on `#/crm/clients/:clientId` with that client's `clientId`. One
 * thread, one contract: `GET /coaching/messages` (+ `?clientId=` for the coach),
 * `POST /coaching/messages`, `GET /coaching/messages/unread`.
 *
 * Deliberately no websockets: the thread polls on a sensible interval while it
 * is mounted and visible, and stops when it is not. A send is optimistic — the
 * bubble appears at once — but a failure keeps the text in place with the API's
 * own `detail` and a retry, so a message is never lost silently.
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { api, type CoachMessage } from '@/lib/api';
import { cn, formatDate } from '@/lib/format';
import { Button } from '@/components/ui';
import { SyncIcon } from '@/lib/icons';
import { EmptyState, LiveMessage, StatusChip } from './components';
/** How often the open thread checks for new messages. */
export const CHAT_POLL_MS = 10_000;

interface LocalMessage extends CoachMessage {
  /** `true` after a failed send; the bubble stays with a retry. */
  failed?: boolean;
}

function isLocal(message: CoachMessage): boolean {
  return message.messageId.startsWith('local-');
}

function byCreatedAt(a: CoachMessage, b: CoachMessage): number {
  const left = Date.parse(a.createdAt);
  const right = Date.parse(b.createdAt);
  if (Number.isNaN(left) || Number.isNaN(right)) return 0;
  return left - right;
}

function initialsOf(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

/** The day pill that separates one conversation day from the next. */
export function chatDayLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  if (sameDay) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();
  if (isYesterday) return 'Yesterday';
  return formatDate(iso);
}

export function chatDayKey(iso: string): string {
  return String(iso).slice(0, 10);
}

/**
 * The counterpart's monogram: a soft accent-tinted circle, the same shape the
 * chatters list and the shell use. Decorative — the name is always in the text.
 */
export function ChatAvatar({
  name,
  size = 'md',
  className,
}: {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const box = size === 'sm' ? 'h-7 w-7 text-[10px]' : size === 'lg' ? 'h-11 w-11 text-[13px]' : 'h-8 w-8 text-[11px]';
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border border-hairline bg-ink/5 font-mono font-bold uppercase tracking-[0.04em] text-ink',
        box,
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}

function messageTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  const clock = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return sameDay ? clock : `${formatDate(iso)} · ${clock}`;
}

export interface CoachChatPanelProps {
  /** The coach's path: the client whose thread this is. Omit for the lifter. */
  clientId?: string | null;
  /** The lifter's path (0028): which of several coaches this thread is with. */
  coachId?: string | null;
  /** Shown when the thread has no messages yet. */
  emptyTitle: string;
  emptyBody: string;
  /** The counterpart's name, used in the composer's label before a payload lands. */
  peerName: string;
  /** Called with the unread count the API reported (it clears as the thread loads). */
  onUnread?: (unread: number) => void;
  testId?: string;
  /** Fill the parent (the standalone chat workspace) instead of capping at 420px. */
  fill?: boolean;
  /**
   * The standalone workspace puts the counterpart's name, avatar and tier in its
   * own header, so the panel must not print a second one.
   */
  hideHeading?: boolean;
}

export function CoachChatPanel({
  clientId,
  coachId,
  emptyTitle,
  emptyBody,
  peerName,
  onUnread,
  testId,
  fill = false,
  hideHeading = false,
}: CoachChatPanelProps) {
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const sequence = useRef(0);
  const onUnreadRef = useRef(onUnread);
  onUnreadRef.current = onUnread;
  // Fluidity: the thread stays pinned to the newest message while the reader is
  // at the bottom, and the composer is focused on open and after a send.
  const listRef = useRef<HTMLUListElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const pinnedRef = useRef(true);

  const merge = useCallback((incoming: CoachMessage[]) => {
    if (incoming.length === 0) return;
    setMessages((previous) => {
      const locals = previous.filter(isLocal);
      const server = new Map(
        previous.filter((entry) => !isLocal(entry)).map((entry) => [entry.messageId, entry]),
      );
      for (const entry of incoming) server.set(entry.messageId, entry);
      return [...[...server.values()].sort(byCreatedAt), ...locals];
    });
    for (const entry of incoming) {
      if (cursorRef.current === null || entry.createdAt > cursorRef.current) {
        cursorRef.current = entry.createdAt;
      }
    }
  }, []);

  const load = useCallback(
    async (incremental: boolean) => {
      const result = await api.coachingMessages({
        clientId: clientId ?? null,
        coachId: coachId ?? null,
        since: incremental ? cursorRef.current : null,
        limit: 200,
      });
      if (result.problem || !result.data) {
        // A badge poll that fails must not blank a thread already on screen.
        if (!incremental) setError(result.problem?.detail ?? result.problem?.title ?? 'Could not read the thread.');
        return;
      }
      setError(null);
      merge(result.data.data);
      onUnreadRef.current?.(result.data.unread);
    },
    [clientId, coachId, merge],
  );

  // First load per thread, then a visibility-aware poll that stops on unmount.
  useEffect(() => {
    let alive = true;
    cursorRef.current = null;
    setMessages([]);
    setLoading(true);
    setLoaded(false);
    setError(null);
    setFailure(null);

    const tick = async () => {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      await load(cursorRef.current !== null);
      if (alive) {
        setLoading(false);
        setLoaded(true);
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), CHAT_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // Fluidity: focus the composer when a thread opens, and keep the newest
  // message in view while the reader is at the bottom — without yanking them
  // back down if they have scrolled up to read history.
  useEffect(() => {
    inputRef.current?.focus();
  }, [clientId, coachId]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !pinnedRef.current) return;
    list.scrollTop = list.scrollHeight;
  }, [messages]);

  const deliver = useCallback(
    async (localId: string, body: string) => {
      setSending(true);
      setFailure(null);
      const result = await api.sendCoachMessage(body, clientId ?? null, coachId ?? null);
      setSending(false);
      if (result.data) {
        const sent = result.data;
        setMessages((previous) =>
          previous.map((entry) => (entry.messageId === localId ? { ...sent } : entry)),
        );
        if (cursorRef.current === null || sent.createdAt > cursorRef.current) {
          cursorRef.current = sent.createdAt;
        }
        return;
      }
      setMessages((previous) =>
        previous.map((entry) => (entry.messageId === localId ? { ...entry, failed: true } : entry)),
      );
      setFailure(
        result.problem?.detail ??
          'That message did not send. It is still here — try again when the API is reachable.',
      );
    },
    [clientId, coachId],
  );

  const send = useCallback(() => {
    const body = draft.trim();
    if (body.length === 0 || sending) return;
    sequence.current += 1;
    const localId = `local-${sequence.current}`;
    const optimistic: LocalMessage = {
      messageId: localId,
      linkId: '',
      senderId: 'me',
      mine: true,
      senderName: 'You',
      body,
      createdAt: new Date().toISOString(),
      readAt: null,
    };
    setMessages((previous) => [...previous, optimistic]);
    setDraft('');
    inputRef.current?.focus();
    void deliver(localId, body);
  }, [deliver, draft, sending]);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const retry = (message: LocalMessage) => {
    if (!message.failed || sending) return;
    setMessages((previous) =>
      previous.map((entry) =>
        entry.messageId === message.messageId ? { ...entry, failed: false } : entry,
      ),
    );
    void deliver(message.messageId, message.body);
  };

  // A run of messages from the same side, close enough in time, reads as one
  // block: no repeated sender label, tighter spacing, the tail only on the last.
  const groups = messages.map((message, index) => {
    const previous = messages[index - 1];
    const sameDay = previous ? chatDayKey(previous.createdAt) === chatDayKey(message.createdAt) : false;
    const sameSide = previous ? previous.mine === message.mine : false;
    const closeInTime = previous
      ? Math.abs(Date.parse(message.createdAt) - Date.parse(previous.createdAt)) < 5 * 60_000
      : false;
    return {
      message,
      separator: index === 0 || !sameDay ? chatDayLabel(message.createdAt) : null,
      firstInRun: !sameSide || !sameDay || !closeInTime,
      next: messages[index + 1],
    };
  });

  return (
    <div className={cn('flex flex-col gap-3', fill && 'h-full min-h-0')} data-testid={testId ?? 'coach-chat'}>
      {hideHeading ? null : (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
          <h3 className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink-muted">
            Chat with {peerName}
          </h3>
          <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
            Live · checks every {Math.round(CHAT_POLL_MS / 1000)}s
          </span>
        </div>
      )}

      {loading && !loaded ? (
        <p className="px-1 text-[13px] text-ink-muted" aria-live="polite" data-testid="chat-loading">
          Loading your messages…
        </p>
      ) : error ? (
        <LiveMessage message={error} tone="danger" />
      ) : messages.length === 0 ? (
        <div data-testid="chat-empty">
          <EmptyState
            className="rounded-chat-lg border-dashed bg-surface/60"
            title={emptyTitle}
            body={emptyBody}
            icon={<ChatIcon className="h-8 w-8 text-accent" />}
          />
        </div>
      ) : (
        <ul
          ref={listRef}
          onScroll={(event) => {
            const el = event.currentTarget;
            pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          }}
          className={cn(
            'flex flex-col overflow-y-auto px-1 pb-1',
            fill ? 'min-h-0 flex-1' : 'max-h-[420px]',
          )}
          data-testid="chat-thread"
        >
          {groups.map(({ message, separator, firstInRun, next }) => {
            const lastInRun = !next || next.mine !== message.mine;
            return (
              <li key={message.messageId} data-testid={`chat-message-${message.messageId}`} data-mine={message.mine ? 'true' : 'false'}>
                {separator ? (
                  <div className="my-3 flex items-center gap-3" data-testid="chat-day-separator">
                    <span className="h-px flex-1 bg-hairline" />
                    <span className="rounded-pill border border-hairline bg-surface px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                      {separator}
                    </span>
                    <span className="h-px flex-1 bg-hairline" />
                  </div>
                ) : null}
                <div
                  className={cn(
                    'flex items-end gap-2',
                    message.mine ? 'justify-end' : 'justify-start',
                    firstInRun ? 'mt-2' : 'mt-1',
                  )}
                >
                  {!message.mine ? (
                    <span className="w-8 shrink-0">
                      {lastInRun ? <ChatAvatar name={message.senderName} size="sm" /> : null}
                    </span>
                  ) : null}
                  <div
                    className={cn(
                      'flex max-w-[78%] flex-col gap-1',
                      message.mine ? 'items-end' : 'items-start',
                    )}
                  >
                    {firstInRun ? (
                      <span className="px-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                        {message.mine ? 'You' : message.senderName}
                        {message.failed ? (
                          <span className="ml-2 normal-case tracking-normal">
                            <StatusChip tone="danger">Not sent</StatusChip>
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    <span
                      data-testid="chat-bubble"
                      data-mine={message.mine ? 'true' : 'false'}
                      className={cn(
                        // No outline: a filled bubble with one tightened tail
                        // corner says who is speaking, at a glance.
                        'flex flex-col gap-1 whitespace-pre-wrap px-3.5 py-2.5 text-[13.5px] leading-relaxed text-ink',
                        message.mine
                          ? 'rounded-bubble rounded-br-chat-tail bg-bubble-mine'
                          : 'rounded-bubble rounded-bl-chat-tail bg-page-subtle',
                      )}
                    >
                      <span>{message.body}</span>
                      <span
                        className={cn(
                          'l2f-tabular font-mono text-[9.5px] leading-none text-ink-muted',
                          message.mine ? 'self-end' : 'self-start',
                        )}
                      >
                        {messageTime(message.createdAt)}
                      </span>
                    </span>
                    {message.failed ? (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`chat-retry-${message.messageId}`}
                        disabled={sending}
                        onClick={() => retry(message)}
                      >
                        Retry
                      </Button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="rounded-chat-lg border border-hairline bg-surface p-2 shadow-float">
        <label className="flex flex-col gap-1.5">
          <span className="sr-only">Message {peerName}</span>
          <textarea
            ref={inputRef}
            value={draft}
            rows={2}
            maxLength={4000}
            data-testid="chat-input"
            aria-label={`Message ${peerName}`}
            placeholder={`Message ${peerName}…`}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            className="l2f-chat-well w-full min-w-0 resize-none rounded-chat px-3.5 py-2.5 text-[14px] text-ink outline-none placeholder:text-ink-muted focus:ring-1 focus:ring-accent"
          />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 pt-2">
          <span className="text-[10.5px] text-ink-muted">
            Enter sends · Shift+Enter adds a line · 4000 characters max
          </span>
          <Button
            data-testid="chat-send"
            size="sm"
            className="rounded-pill"
            disabled={sending || draft.trim().length === 0}
            onClick={send}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>
        <LiveMessage message={failure} tone="danger" />
      </div>
    </div>
  );
}

/**
 * The new-window buttons into the standalone chat workspace (0028 IA). One
 * implementation serves both sides: the lifter's total-unread entry on My
 * Coach, and the coach's entry on the CRM Clients page and a client's page
 * (focused on that client). `variant="primary"` is the orange page CTA; the
 * default outline is the quieter, secondary entry.
 */
type ChatWindowTone = 'outline' | 'primary';

const CHAT_WINDOW_BASE =
  'inline-flex items-center gap-2 font-bold uppercase tracking-[0.08em] transition-colors duration-150';

const CHAT_WINDOW_TONES: Record<ChatWindowTone, string> = {
  outline: 'h-11 border border-hairline bg-surface px-3 text-[11px] text-ink hover:border-accent hover:text-accent',
  primary: 'h-12 bg-accent px-6 text-[13px] text-on-accent hover:bg-accent-hover',
};

/** The badge inside a primary (orange) button needs its own contrast; on the
 * outline button the orange badge is the accent. */
const CHAT_WINDOW_BADGE_TONES: Record<ChatWindowTone, string> = {
  outline: 'bg-accent text-on-accent',
  primary: 'bg-brand-charcoal text-brand-white',
};

/** Polls the caller's unread count, paused while the tab is hidden. */
function useChatUnread(query: { clientId?: string | null; coachId?: string | null }): number {
  const [unread, setUnread] = useState(0);
  const key = query.clientId ?? query.coachId ?? '';
  const queryRef = useRef(query);
  queryRef.current = query;
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const result = await api.coachingUnread(queryRef.current);
      if (alive && result.data) setUnread(result.data.unread);
    };
    void tick();
    const timer = window.setInterval(() => void tick(), CHAT_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [key]);
  return unread;
}

function ChatWindowLink({
  href,
  testId,
  badgeTestId,
  title,
  unread,
  variant,
  className,
}: {
  href: string;
  testId: string;
  badgeTestId: string;
  title: string;
  unread: number;
  variant: ChatWindowTone;
  className?: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      data-testid={testId}
      title={title}
      className={cn(CHAT_WINDOW_BASE, CHAT_WINDOW_TONES[variant], className)}
    >
      <ChatIcon className="text-[14px]" />
      Chat
      {unread > 0 ? (
        <span
          data-testid={badgeTestId}
          className={cn(
            'l2f-tabular rounded-pill px-1.5 py-0.5 font-mono text-[10px] font-bold',
            CHAT_WINDOW_BADGE_TONES[variant],
          )}
        >
          {unread}
        </span>
      ) : null}
    </a>
  );
}

/**
 * The coach's entry to the same workspace, as a labelled button. Without
 * `clientId` it opens the coach side on every thread (the Clients page); with
 * one it deep-links to that client's thread (their page).
 */
export function CoachChatWindowButton({
  clientId = null,
  testId = 'coach-chat-window',
  badgeTestId = 'coach-chat-window-badge',
  className,
  variant = 'outline',
}: {
  clientId?: string | null;
  testId?: string;
  badgeTestId?: string;
  className?: string;
  variant?: ChatWindowTone;
}) {
  const unread = useChatUnread({ clientId });
  return (
    <ChatWindowLink
      href={clientId ? `#/chat?clientId=${encodeURIComponent(clientId)}` : '#/chat'}
      testId={testId}
      badgeTestId={badgeTestId}
      title={clientId ? 'Open this thread in the chat workspace' : 'Open the chat workspace in a new window'}
      unread={unread}
      variant={variant}
      className={className}
    />
  );
}

/**
 * The lifter's icon-only chat entry in the shell header — the same 40×40 shape
 * as the theme toggle and the coach's and admin's chat buttons. It sits in the
 * top bar on every lifter screen (dashboard, Grid, My Coach …) instead of a
 * separate button on the My Coach page, and carries the TOTAL unread badge so an
 * inquiry thread to any coach is visible from anywhere in the app.
 */
export function LifterChatIconButton({ className }: { className?: string }) {
  const unread = useChatUnread({});
  const label =
    unread > 0
      ? `Open your chats in a new window — ${unread} unread`
      : 'Open your chats in a new window';
  return (
    <a
      href="#/chat?side=lifter"
      target="_blank"
      rel="noopener"
      data-testid="lifter-chat-button"
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-card border border-hairline',
        'text-ink transition-colors duration-150 hover:border-accent hover:text-accent',
        className,
      )}
    >
      <ChatIcon className="h-[18px] w-[18px]" />
      {unread > 0 ? (
        <span
          data-testid="lifter-chat-button-badge"
          className="l2f-tabular absolute -right-1 -top-1 min-w-[16px] rounded-pill bg-accent px-1 text-center font-mono text-[9px] font-bold leading-4 text-on-accent"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </a>
  );
}

/**
 * The coach's icon-only chat entry in the CRM shell header (0028 polish): the
 * same 40×40 shape as the theme toggle, sitting beside it, carrying only the
 * chat glyph and an unread badge. It opens the standalone workspace in a new
 * window (`#/chat`), matching every other entry.
 */
export function CoachChatIconButton({ className }: { className?: string }) {
  const unread = useChatUnread({});
  const label =
    unread > 0
      ? `Open the chat workspace in a new window — ${unread} unread`
      : 'Open the chat workspace in a new window';
  return (
    <a
      href="#/chat"
      target="_blank"
      rel="noopener"
      data-testid="crm-chat-window"
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-card border border-hairline',
        'text-ink transition-colors duration-150 hover:border-accent hover:text-accent',
        className,
      )}
    >
      <ChatIcon className="h-[18px] w-[18px]" />
      {unread > 0 ? (
        <span
          data-testid="crm-chat-window-badge"
          className="l2f-tabular absolute -right-1 -top-1 min-w-[16px] rounded-pill bg-accent px-1 text-center font-mono text-[9px] font-bold leading-4 text-on-accent"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </a>
  );
}

/** A small local chat glyph — the shared icon set has no message mark. */
export function ChatIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn('h-4 w-4', className)}
    >
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.6 9.6 0 0 1-2.8-.4L4 21l1.5-4.2A8.4 8.4 0 1 1 21 11.5Z" />
    </svg>
  );
}
